import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { authService } from '../../services';
import {
  setClinicToken,
  getClinicToken,
  setRefreshToken,
  getRefreshToken,
  clearAuthTokens,
} from '../../services/http';
import { CLINIC_STORAGE_KEYS } from '../../constants/buyer-portal';
import {
  ApiError,
  CurrentUser,
  LoginRequest,
  RegisterBuyerRequest,
  RegisterBuyerResult,
} from '../../types';
import { isWeb } from '../../utilities/platform';
import { isInNativeApp, notifyLogout } from '../../bridge';
import type { CartStore } from './cartStore';
import type { BusinessStore } from './businessStore';
import type { MembershipStore } from './membershipStore';
import type { InvitationStore } from './invitationStore';
import type { OrderStore } from './orderStore';

const readStoredUser = (): CurrentUser | null => {
  if (!isWeb) return null;
  const raw = localStorage.getItem(CLINIC_STORAGE_KEYS.USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
};

const writeStoredUser = (user: CurrentUser | null): void => {
  if (!isWeb) return;
  if (user) localStorage.setItem(CLINIC_STORAGE_KEYS.USER, JSON.stringify(user));
  else localStorage.removeItem(CLINIC_STORAGE_KEYS.USER);
};

export type IdentityStores = {
  business: BusinessStore;
  membership: MembershipStore;
  invitation: InvitationStore;
};

export class AuthStore {
  currentUser: CurrentUser | null = null;
  signingIn = false;
  registering = false;
  loadingMe = false;
  error: ApiError | null = null;

  private cartStoreRef: CartStore | null = null;
  private identityStoresRef: IdentityStores | null = null;
  private orderStoreRef: OrderStore | null = null;

  constructor() {
    makeObservable(this, {
      currentUser: observable,
      signingIn: observable,
      registering: observable,
      loadingMe: observable,
      error: observable,
      isAuthed: computed,
      isUserActive: computed,
      currentBusiness: computed,
      hasActiveBusiness: computed,
      isCurrentBusinessActive: computed,
      signIn: action,
      register: action,
      signOut: action,
      hydrate: action,
      refreshMe: action,
      replaceTokensAndRefresh: action,
      clearError: action,
    });
    this.hydrate();
  }

  /** Allows the root store to wire cart-mode side effects without circular imports. */
  attachCartStore(cart: CartStore): void {
    this.cartStoreRef = cart;
    cart.setMode(this.isAuthed ? 'authed' : 'guest');
  }

  /** Wires identity stores so we can clear them on sign-out / membership change. */
  attachIdentityStores(stores: IdentityStores): void {
    this.identityStoresRef = stores;
  }

  /** Wires order store so we can reset cached orders on sign-in / sign-out. */
  attachOrderStore(order: OrderStore): void {
    this.orderStoreRef = order;
  }

  get isAuthed(): boolean {
    return !!this.currentUser && !!getClinicToken();
  }

  get isUserActive(): boolean {
    return this.currentUser?.status === 'ACTIVE';
  }

  get currentBusiness(): CurrentUser['currentBusiness'] {
    return this.currentUser?.currentBusiness ?? null;
  }

  get hasActiveBusiness(): boolean {
    return !!this.currentBusiness;
  }

  /**
   * Buyer purchase identity used to pick the catalogue price tier.
   * Not logged in → `GUEST`; logged in with an ACTIVE membership → `BUSINESS`;
   * logged in without one → `INDIVIDUAL`. Guests are priced as Individuals.
   */
  get buyerIdentity(): 'BUSINESS' | 'INDIVIDUAL' | 'GUEST' {
    if (!this.isAuthed) return 'GUEST';
    return this.hasActiveBusiness ? 'BUSINESS' : 'INDIVIDUAL';
  }

  /**
   * `true` when there is an active membership and the underlying business is ACTIVE.
   * Use this in route guards that *require* a Business context (e.g. checkout flows
   * that must produce a Business order). Individual users return `false` here —
   * which is fine, because they should not be gated by business status.
   */
  get isCurrentBusinessActive(): boolean {
    if (!this.identityStoresRef?.business.business) return this.hasActiveBusiness;
    return this.identityStoresRef.business.isOwnedBusinessActive;
  }

  hydrate(): void {
    this.currentUser = readStoredUser();
    // If a session token exists, silently re-verify identity against /auth/me.
    // CLAUDE.md: identity must be resolved in real time, not from cache, so
    // role/status/membership changes server-side are picked up on app boot.
    // The 401 interceptor will clear tokens if the session is no longer valid.
    if (getClinicToken()) {
      this.refreshMe().catch(() => undefined);
    }
  }

  clearError(): void {
    this.error = null;
  }

  async signIn(req: LoginRequest): Promise<CurrentUser> {
    this.signingIn = true;
    this.error = null;
    try {
      const res = await authService.login(req);
      setClinicToken(res.accessToken);
      setRefreshToken(res.refreshToken);
      writeStoredUser(res.user);
      runInAction(() => {
        this.currentUser = res.user;
      });
      // Discard local guest cart; switch cart store to server-backed mode.
      const cart = this.cartStoreRef;
      if (cart) {
        cart.clearLocal();
        cart.setMode('authed');
        cart.load().catch(() => undefined);
      }
      this.orderStoreRef?.reset();
      return res.user;
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.signingIn = false;
      });
    }
  }

  async register(req: RegisterBuyerRequest): Promise<RegisterBuyerResult> {
    this.registering = true;
    this.error = null;
    try {
      const result = await authService.register(req);
      // Backend auto-logs the user in on register. Mirror sign-in: pull the
      // freshly minted /auth/me so `isAuthed` flips true and downstream pages
      // (invitation accept, onboarding, dashboard) see a populated user.
      try {
        await this.refreshMe();
      } catch {
        /* best-effort; caller can still navigate to /login */
      }
      return result;
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.registering = false;
      });
    }
  }

  async refreshMe(): Promise<CurrentUser | null> {
    const token = getClinicToken();
    if (!token) return null;
    this.loadingMe = true;
    try {
      const user = await authService.me(token);
      writeStoredUser(user);
      runInAction(() => {
        this.currentUser = user;
      });
      return user;
    } catch (err) {
      // If the 401 interceptor already cleared tokens (refresh also failed),
      // drop the stale cached user so route guards send the user to login.
      if (!getClinicToken()) {
        writeStoredUser(null);
        runInAction(() => {
          this.currentUser = null;
        });
      }
      runInAction(() => {
        this.error = err as ApiError;
      });
      return null;
    } finally {
      runInAction(() => {
        this.loadingMe = false;
      });
    }
  }

  /**
   * Used after `accept-invitation` and `leave-business` flows where the backend
   * issues a fresh token bundle reflecting the new identity. Persists the new
   * access token, refetches `/auth/me`, and resets dependent identity stores.
   */
  async replaceTokensAndRefresh(accessToken: string): Promise<CurrentUser | null> {
    setClinicToken(accessToken);
    if (this.identityStoresRef) {
      this.identityStoresRef.business.clear();
      this.identityStoresRef.membership.clear();
      this.identityStoresRef.invitation.clear();
    }
    this.orderStoreRef?.reset();
    return this.refreshMe();
  }

  async signOut(): Promise<void> {
    // Inside the Expo WebView, native owns token storage and will call the
    // backend logout itself (per BRIDGE.md clean-up checklist). Hand off and
    // skip the web-side logout call so we don't double-revoke.
    if (isInNativeApp()) {
      const ok = await notifyLogout();
      if (ok) return;
      // Bridge failed — fall through to the web flow so the user isn't stuck.
    }
    const refreshToken = getRefreshToken();
    try {
      await authService.logout(refreshToken);
    } catch {
      // ignore — clear local state regardless
    }
    clearAuthTokens();
    writeStoredUser(null);
    runInAction(() => {
      this.currentUser = null;
      this.error = null;
    });
    const cart = this.cartStoreRef;
    if (cart) {
      cart.clearLocal();
      cart.setMode('guest');
    }
    if (this.identityStoresRef) {
      this.identityStoresRef.business.clear();
      this.identityStoresRef.membership.clear();
      this.identityStoresRef.invitation.clear();
    }
    this.orderStoreRef?.reset();
  }
}
